import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'
import { Button } from '../ui/Button'
import { Textarea } from '../ui/Textarea'
import { DreamService, type Dream, type UpdateDreamRequest } from '../../services/dreamService'
import { LIFE_AREA_OPTIONS, type Choice } from '../../utils/taskForm'
import { DREAM_PRIORITY_OPTIONS } from '../../utils/dreamOptions'

/**
 * "Fill the gaps" for a dream (the rail's first block on every item page).
 * One question at a time, as a carousel — Next/Back move, answering closes
 * the gap, nothing is put away. The gaps: why it matters, which parts of
 * life, by when, how much it matters. Answering writes the field.
 */
interface Props {
  dream: Dream
  onChanged: (dream: Dream) => void
  embedded?: boolean
}

type GapKind = 'why' | 'areas' | 'by' | 'priority'
interface Gap { kind: GapKind; question: string; hint: string }

function gapsOf(d: Dream): Gap[] {
  const out: Gap[] = []
  if (!d.description) out.push({ kind: 'why', question: 'What does life look like when this is true?', hint: 'helps me keep you going' })
  if (!(d.life_areas && d.life_areas.length) && !d.life_area) out.push({ kind: 'areas', question: 'Which parts of life is it for?', hint: 'helps me plan the journey' })
  if (!d.target_date) out.push({ kind: 'by', question: 'By when, roughly? A season or a year is fine.', hint: 'helps me pace the journey' })
  if (!d.priority || d.priority === 'medium') out.push({ kind: 'priority', question: 'How much does it matter right now?', hint: 'helps me plan it' })
  return out
}

function Chips({ options, value, onChange, label, multi = false }: { options: Choice[]; value: string[]; onChange: (v: string[]) => void; label: string; multi?: boolean }) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
      {options.map((o) => {
        const on = value.includes(o.value)
        return (
          <button key={o.value} type="button" aria-pressed={on}
                  onClick={() => onChange(on ? value.filter((v) => v !== o.value) : multi ? [...value, o.value] : [o.value])}
                  className={`min-h-touch rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${on ? 'border-brand bg-brand text-on-brand' : 'border-strong bg-surface text-secondary hover:border-brand'}`}>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export function DreamGapsCard({ dream, onChanged, embedded = false }: Props) {
  const gaps = useMemo(() => gapsOf(dream), [dream])
  const [idx, setIdx] = useState(0)
  const [answer, setAnswer] = useState<{ text?: string; areas?: string[]; date?: string; priority?: string }>({})
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => { if (idx > gaps.length - 1) setIdx(Math.max(0, gaps.length - 1)) }, [gaps.length, idx])
  useEffect(() => { setAnswer({}); setErr(null) }, [idx])
  const gap = gaps[idx] ?? null
  const go = (d: number) => { if (gaps.length) setIdx((i) => (i + d + gaps.length) % gaps.length) }

  const save = async (payload: UpdateDreamRequest) => {
    setBusy(true); setErr(null)
    try {
      const svc = new DreamService()
      await svc.updateDream(dream.id, payload)
      onChanged(await svc.getDream(dream.id))
      setAnswer({})
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that')
    } finally {
      setBusy(false)
    }
  }

  const submit = async () => {
    if (!gap) return
    let payload: UpdateDreamRequest | null = null
    switch (gap.kind) {
      case 'why': { const t = (answer.text || '').trim(); if (t) payload = { description: t }; break }
      case 'areas': if (answer.areas && answer.areas.length) payload = { life_areas: answer.areas }; break
      case 'by': if (answer.date) payload = { target_date: answer.date }; break
      case 'priority': if (answer.priority) payload = { priority: answer.priority as UpdateDreamRequest['priority'] }; break
    }
    if (!payload) { setErr('Pick or type an answer first.'); return }
    await save(payload)
  }

  return (
    <div className={embedded ? '' : 'rounded-xl border border-brand-subtle bg-surface p-4 sm:p-5'} data-testid="dream-gaps-card">
      <div className="flex items-center gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-primary">{gap ? 'Fill the gaps' : 'Nothing missing'}</div>
          {/* No count once nothing is missing: "4 of 4 said" answered no question (rule 7). */}
          <div className="text-xs text-muted">{gap ? `${gaps.length} detail${gaps.length === 1 ? '' : 's'} missing` : 'Everything the journey needs is here.'}</div>
        </div>
        {gaps.length > 1 && (
          <div className="ml-auto flex items-center gap-1 text-app-micro text-muted" data-testid="dream-gaps-nav">
            <button type="button" onClick={() => go(-1)} aria-label="Previous question" className="rounded p-1 hover:bg-surface hover:text-primary"><ChevronLeft className="h-4 w-4" /></button>
            <span className="tabular-nums">{idx + 1} of {gaps.length}</span>
            <button type="button" onClick={() => go(1)} aria-label="Next question" className="rounded p-1 hover:bg-surface hover:text-primary"><ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      {gap && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-primary" data-testid="dream-gaps-question">{gap.question}</p>
          {gap.kind === 'why' && (
            <Textarea rows={2} value={answer.text ?? ''} onChange={(e) => setAnswer({ ...answer, text: e.target.value })} placeholder="In your words — one line is plenty" aria-label={gap.question} />
          )}
          {gap.kind === 'areas' && <Chips label="Life areas" multi options={LIFE_AREA_OPTIONS} value={answer.areas || []} onChange={(v) => setAnswer({ ...answer, areas: v })} />}
          {gap.kind === 'by' && (
            <input type="date" value={answer.date || ''} onChange={(e) => setAnswer({ ...answer, date: e.target.value })} aria-label="Get there by"
                   className="min-h-touch rounded-md border border-strong bg-surface px-2 text-sm text-primary lg:min-h-0 lg:py-1.5" />
          )}
          {gap.kind === 'priority' && <Chips label="Priority" options={DREAM_PRIORITY_OPTIONS} value={answer.priority ? [answer.priority] : []} onChange={(v) => setAnswer({ ...answer, priority: v[0] })} />}

          {err && <p className="text-xs text-bad" role="alert">{err}</p>}
          <div className="flex items-center gap-2">
            <Button size="xs" variant="outline" className="hover:bg-brand-subtle" onClick={() => void submit()} disabled={busy} data-testid="dream-gaps-save">Save</Button>
            {gaps.length > 1 && <button type="button" onClick={() => go(1)} disabled={busy} className="min-h-touch px-3 text-sm text-muted hover:text-secondary" data-testid="dream-gaps-next">Next question</button>}
            <span className="ml-auto inline-flex items-center gap-1 text-app-micro text-muted"><Sparkles className="h-3 w-3" aria-hidden="true" /> {gap.hint}</span>
          </div>
          {gaps.length > 1 && (
            <div className="flex items-center justify-center gap-1.5" aria-hidden="true">
              {gaps.map((g, i) => <button key={g.kind} type="button" tabIndex={-1} onClick={() => setIdx(i)} title={g.question} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-4 bg-brand' : 'w-1.5 bg-soft hover:bg-strong'}`} />)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DreamGapsCard
